import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import TextButton from 'LuxKit/Button/TextButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineExclamationTriangleIcon from 'LuxKit/Icons/line/LineExclamationTriangleIcon'
import Label from 'LuxKit/Label/Label'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { pushWithRegion } from 'actions/NavigationActions'
import useOffer from 'hooks/Offers/useOffer'
import { trackClientEvent } from 'analytics/analytics'
import { extractOfferDisplayData } from 'supportAssistant/utils/offerDisplayUtils'

const CardContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  width: 100%;
  max-width: ${rem(320)};
  display: flex;
  flex-direction: column;

  @container nls-cards (min-width: ${rem(340)}) {
    flex-direction: row;
    max-width: ${rem(440)};
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(140)};
  flex-shrink: 0;
  overflow: hidden;

  @container nls-cards (min-width: ${rem(340)}) {
    width: ${rem(160)};
    height: auto;
    min-height: ${rem(160)};
  }
`

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg,
    ${props => props.theme.palette.neutral.default.six} 0%,
    ${props => props.theme.palette.neutral.default.five} 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`

const ErrorPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: ${props => props.theme.palette.messaging.warning.lightBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`

const ContentContainer = styled(Group)`
  padding: ${rem(16)};
  flex: 1;
  min-width: 0;
`

const HeaderRow = styled(Group)`
  width: 100%;
`

const PropertyName = styled(BodyText)<{ $interactive?: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  cursor: ${props => props.$interactive ? 'pointer' : 'default'};

  &:hover {
    text-decoration: ${props => props.$interactive ? 'underline' : 'none'};
  }
`

const LocationText = styled(CaptionTextBlock)`
  flex: 1;
  min-width: 0;
`

const PackageName = styled(CaptionTextBlock)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const HotelLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
`

interface Props {
  property: App.NlsPropertyMatch
  index: number
  nlsOfferParams?: string
  nlsCarouselVariation?: string
}

export default function LesaNlsPropertyCard({
  property,
  index,
  nlsOfferParams,
  nlsCarouselVariation,
}: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const [offer, loading, error] = useOffer<App.Offer | App.BedbankOffer>(property.offerId)

  const hasError = !!error && !loading

  const offerDetails = useMemo(() => {
    if (!offer) return null
    return extractOfferDisplayData(offer, intl)
  }, [offer, intl])

  const selectButtonText = loading ? intl.formatMessage({
    id: 'fESuXBZdyPwHXGFticya',
    defaultMessage: 'Loading...',
    description: 'Loading state text for hotel offer button on the offer page',
  }) : hasError ? intl.formatMessage({
    id: 'JhWmZQTuMyqR4Dk3_a4e',
    defaultMessage: 'Unavailable',
    description: 'Button text when hotel offer is unavailable due to error',
  }) : intl.formatMessage({
    id: 'irM4d6Gs4xeamACcVrNN',
    defaultMessage: 'View offer',
    description: 'Button text to view a hotel offer on the hotel or room booking page',
  })

  const isInteractive = !loading && !hasError && !!offerDetails

  const handleSelect = useCallback(() => {
    if (!isInteractive) return
    const eventVariation = nlsCarouselVariation ?? 'not_bucketed'

    const propertyName = offerDetails?.propertyName || intl.formatMessage({
      id: 'ZfUCncK6UvoqAGYEesBk',
      defaultMessage: 'this hotel',
      description: 'Fallback name for a hotel property when the actual name is not available',
    })
    const location = offerDetails?.location

    trackClientEvent({
      subject: 'support_assistant',
      action: `lesa_nls_offer_clicked_${eventVariation}`,
      category: 'logging',
      type: 'interaction',
      optimizelyEventId: '4557425865392128',
      optimizelyEventKey: 'lesa_nls_offer_click',
    })

    dispatch(addChatMessage(intl.formatMessage({
      id: 'KS6rLRpWRSnSvriGfRsK',
      defaultMessage: 'Show me {propertyName}',
      description: 'Chat message sent when user selects a hotel property to view',
    }, { propertyName }), {
      action: 'selectNlsProperty',
      payload: {
        offerId: property.offerId,
        propertyName,
        location,
        matchIndex: index + 1,
      },
    }))
    dispatch(pushWithRegion(`/offer/${property.offerId}`, nlsOfferParams, { fromSupportChat: true }))
  }, [dispatch, property, index, offerDetails, isInteractive, intl, nlsCarouselVariation, nlsOfferParams])

  const renderImage = () => {
    if (loading) {
      return <LoadingBox width={320} height={140} />
    }
    if (hasError) {
      return (
        <ErrorPlaceholder>
          <LineExclamationTriangleIcon size="L" colour="warning" />
        </ErrorPlaceholder>
      )
    }
    if (offerDetails?.imageId) {
      return (
        <Image
          id={offerDetails.imageId}
          alt={offerDetails.propertyName}
          fit="center"
          objectFit="cover"
          width={320}
          height={200}
        />
      )
    }
    return (
      <ImagePlaceholder>
        <LineBedIcon size="L" colour="neutral-three" />
      </ImagePlaceholder>
    )
  }

  const renderContent = () => {
    if (loading) {
      return (
        <Group direction="vertical" gap={8}>
          <LoadingBox width={180} height={20} borderRadius="XS" />
          <LoadingBox width={140} height={16} borderRadius="XS" />
        </Group>
      )
    }
    if (hasError) {
      return (
        <CaptionTextBlock variant="medium" colour="neutral-three">
          {intl.formatMessage({
            id: 'nY4i_AhUhgb8dH-jOFyH',
            defaultMessage: 'Unable to load offer details',
            description: 'Error message when hotel offer details fail to load',
          })}
        </CaptionTextBlock>
      )
    }
    return (
      <>
        <PropertyName
          variant="medium"
          weight="bold"
          $interactive={isInteractive}
          onClick={handleSelect}
        >
          {offerDetails?.propertyName || intl.formatMessage(productLabels.hotel)}
        </PropertyName>

        {offerDetails?.packageName && (
          <PackageName variant="small" colour="neutral-two">
            {offerDetails.packageName}
          </PackageName>
        )}

        {offerDetails?.location && (
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <LineMapMarkerIcon size="XS" colour="neutral-three" />
            <LocationText variant="medium" colour="neutral-three" lineClamp={1}>
              {offerDetails.location}
            </LocationText>
          </Group>
        )}
      </>
    )
  }

  return (
    <CardContainer>
      <ImageContainer>
        {renderImage()}
      </ImageContainer>

      <ContentContainer direction="vertical" gap={8} verticalAlign="space-between">
        <Group direction="vertical" gap={8}>
          <HeaderRow direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
            <HotelLabel>
              <LineBedIcon size="XS" colour="neutral-two" />
              <CaptionTextBlock variant="medium" colour="neutral-two">{intl.formatMessage(productLabels.hotel)}
              </CaptionTextBlock>
            </HotelLabel>
            <Label kind="primary" variant="highlight">{property.matchLabel}</Label>
          </HeaderRow>

          {renderContent()}
        </Group>

        <Group direction="horizontal" horizontalAlign="end" fullWidth>
          <TextButton
            onClick={handleSelect}
            size="small"
            kind="secondary"
            disabled={!isInteractive}
          >
            {selectButtonText}
          </TextButton>
        </Group>
      </ContentContainer>
    </CardContainer>
  )
}
